<template>
    <cms-block v-bind="$props" @block-selected="selectBlock" :style="blockStyle" container-class="search-header">
        <cms-component-text comp-id="title" :component="block.title" placeholder="Title|Titre" v-bind="componentProps"/>
        <cms-component-text comp-id="subtitle" :component="block.subtitle" placeholder="Subtitle|Sous-titre" v-bind="componentProps"/>
        <e-search-input :placeholder="tr(block.placeholder)" @change="onSearched" @clear="clearSearch" class="mt mb-2" :autofocus="block.mediaProxy.autofocus"
                        :class="block.mediaProxy.fillWidth ? 'fill-width': 'auto-width'" :size="block.mediaProxy.size" v-model="search" :readonly="isEditMode"/>

        <div class="search-results" :mode="block.container" :show-limit="isSelected && !isChild" :style="containerStyle" :align="block.mediaProxy.align">
            <cms-block-list-item v-for="(item, index) in searchResults" :key="`list-item-${item.id}`" class="cms-list-item" :block-component="bodyComponent" no-body :item="item"
                                 :id-suffix="index" @click.native="selectBlock(item, child)" @clicked="itemClicked(item)"
                                 :block="child" :interactive="!isEditMode" :parent="block" :block-selection="index === 0 ? blockSelection : null" :editable="editable && index === 0">
                <b-badge variant="light" class="md keyword text-dark mt" pill v-if="block.displayKeywords && search && item.getMatchingKeywords(searchRegexp)">
                    <i class="fa-search text-muted" :class="fontAwesomePrefix" style="margin-left: -3px"/> {{item.getMatchingKeywords(searchRegexp)}}
                </b-badge>
            </cms-block-list-item>
        </div>

    <!--        <item-summary v-if="itemSelected" :item="itemSelected" @hidden="unselectItem"/>-->

            <template v-if="isKioskDevice">
                <virtual-keyboard v-show="!itemSelected" ref="keyboard" theme="transparent" v-model="search" @change="onKeyboardChange" @delete="clearSearch" @enter="onEnter"/>
            </template>
    </cms-block>
</template>

<script>
import ItemSummary from "@/components/cms/item-summary.vue";
import CmsBlock from "@/components/editor/rendering/blocks/cms-block.vue";
import CmsBlockRenderer from "@/components/editor/rendering/blocks/cms-block-renderer.vue";
import CmsComponentText from "@/components/editor/rendering/components/cms-component-text.vue";
import VirtualKeyboard from "@/components/virtual-keyboard.vue";
import {swal} from "vue-swal";
import CmsAnalyticsSearch from "../../../../../common/db/models/cms/CmsAnalyticsSearch.js";
import ESearchInput from "../../../../../vue-components/components/e-search-input.vue";
import NetworkV2 from "../../../../../vue-components/helpers/NetworkV2.js";
import CmsBlockListItem from "@/components/editor/rendering/blocks/cms-block-list-item.vue";
import SessionTracker from "@/helpers/SessionTracker.js";

// cms-block-search #hot-reload-debug
export default {
    name: `cms-block-search`,
    extends: CmsBlock,
    components: {
        CmsBlockRenderer,
        CmsComponentText,
        VirtualKeyboard,
        ItemSummary,
        ESearchInput,
        CmsBlockListItem,
        CmsBlock
    },
    data() {
        return {
            search: ``,
            analyticsSearch: null,
            analyticsDebounce: null,
            itemSelected: null
        }
    },
    computed: {
        child() {
            return this.block.children[0];
        },
        bodyComponent() {
            if (this.block.mediaProxy.layout === `cards`) {
                return `b-card`;
            } else {
                return `div`;
            }
        },
        blockStyle() {
            let style = {
                "--list-item-spacing": this.block.mediaProxy.spacing,
                "--list-item-columns": this.block.mediaProxy.columns
            }
            if (this.block.divider.active) {
                style[`--divider-width`] = this.block.divider.mediaProxy.width;
                style[`--divider-color`] = this.block.divider.mediaProxy.color;
            }
            if (this.block.mediaProxy.maxHeight !== `none`) {
                style[`--search-max-height`] = this.block.mediaProxy.maxHeight;
            }
            style[`--border-radius`] = this.block.mediaProxy.borderRadius;
            return style;
        },
        containerStyle() {
            let style = {};

            if (this.block.background === `color` && this.block.backgroundColor) {
                // style[`--scheme-bg`] = this.cmsWebsite.getColor(`background`);
                style.background = this.block.mediaProxy.colorScheme.background;
            }

            if (this.block.borders?.active) {
                style.borderWidth = this.block.borders.mediaProxy.width;
                style.borderColor = this.block.borders.mediaProxy.color;
                style.borderStyle = this.block.borders.mediaProxy.style;
            }

            return style;
        },
        searchRegexp() {
            const normalizedSearch = this.search.normalize(`NFKD`).replace(/\W/g, ``);
            return new RegExp(normalizedSearch, `gi`);
        },
        searchResults() {
            let list = [];
            if (this.block.itemType === `inherit`) {
                list = this.cms.getItemsByType(this.cmsPage.itemType);
            } else {
                list = this.cms.getItemsByType(this.block.itemType);
            }
            if (!this.search || this.search.length < 2) {
                if (this.isEditMode || !this.block.displayOnSearchOnly) {
                    if (list.length > this.block.mediaProxy.maxSearchResult) {
                        return list.slice(0, this.block.mediaProxy.maxSearchResult);
                    } else {
                        return list;
                    }
                } else {
                    return null;
                }
            } else {
                list = list.filter(l => l.matchSearch(this.searchRegexp));
                if (list.length > this.block.mediaProxy.maxSearchResult) {
                    return list.slice(0, this.block.mediaProxy.maxSearchResult);
                } else {
                    return list;
                }
            }
        }
    },
    activated() {
        this.analyticsSearch = null;
    },
    methods: {
        onEnter() {
            console.log(`submitted`);
            if (!this.search) {
                swal({
                    title: this.$tr(`What are you looking for?|Que cherchez-vous ?`),
                    text: this.$tr(`Please type the first letters of the department you are looking for|Merci d'écrire les premières lettres de ce que vous cherchez`),
                    icon: `info`
                });
            } else if (this.searchResults.length > 0) {
                swal({
                    title: this.$tr(`Found ${this.searchResults.length}|Trouvé ${this.searchResults.length} résultats`),
                    text: this.$tr(`Tap on the result to view it's location|Appuyez sur un des résultats pour afficher son emplacement`),
                    icon: `info`
                });
            } else if (this.searchResults.length === 0) {
                swal({
                    title: this.$tr(`Couldn't find|Nous n'avons rien trouvé`),
                    text: this.$tr(`Try with the name of the department instead|Essayez avec le nom du département à la place`),
                    icon: `info`
                });
            }
        },
        onSearched() {
            if (this.search) {
                this.analyticsSearch = new CmsAnalyticsSearch({
                    sessionId: SessionTracker.session.id,
                    websiteId: this.cmsWebsite.id,
                    dealerid: this.dealerid,
                    search: this.search,
                    results: this.searchResults.length
                });

                this.sendAnalytics();
            }
        },
        itemClicked(item) {
            if (this.isEditMode)
                return;

            // TODO: fix issue where itemClicked is not triggered 100% of time. Really hard to reproduce, no known pattern
            if (item && this.analyticsSearch) {
                this.analyticsSearch.item = {
                    id: item.id,
                    name: item.name,
                    type: item.type
                };
                const keywords = this.getMatchingKeywords(item, false);
                if (keywords) {
                    this.analyticsSearch.keywords = keywords;
                }

                if (this.analyticsSearch.id && this.analyticsSearch.search === this.search) {
                    // add to existing analytics search
                    NetworkV2.put(`/api/public/analytics/searches/item-clicked`, this.analyticsSearch);
                } else {
                    this.sendAnalytics();
                }
            }
        },
        sendAnalytics() {
            // use debounce to handle situation when we click on item while still having focus on search to send one request
            if (this.analyticsDebounce) {
                clearTimeout(this.analyticsDebounce);
            }
            this.analyticsDebounce = setTimeout(() => {
                NetworkV2.post(`/api/public/analytics/searches/add`, this.analyticsSearch)
                    .then(resp => {
                        this.analyticsSearch.id = resp.data.id;
                    });
            }, 500);
        },
        onKeyboardChange(input) {
            this.search = input;
        },
        clearSearch() {
            if (this.$refs.keyboard) {
                this.$refs.keyboard.clear();
            }
            this.search = ``;
            if (this.$refs.search) {
                this.$refs.search.focus();
            }
        },
        selectBlock() {
            if (this.isEditMode) {
                this.$emit(`block-selected`, this.child);
            }
        },
        getMatchingKeywords(item, asString = true) {
            if (!this.search) {
                return null;
            } else {
                return item.getMatchingKeywords(this.searchRegexp, asString);
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.cms-block-search {
    --divider-width: 0;
    --divider-color: 0;
    --list-item-columns: 0;
    --list-item-spacing: 10px;
    --search-max-height: none;

    ::v-deep .search-header {
        column-count: initial !important;
        widows: initial !important;
        orphans: initial !important;
    }

    .keyword {
        pointer-events: none;
    }

    ::v-deep .e-search-input {
        &.fill-width {
            width: 100%;
        }
        &.auto-width {
            width: 300px;
            max-width: 100%;
        }

        &.size-xxl {
            .search-icon, .clear-icon {
                width: 60px;
            }
            .form-control {
                padding-inline: 60px;
            }
        }

        &.size-lg {
            &.auto-width {
                width: 400px;
            }
            .search-icon, .clear-icon {
                width: 44px;
            }
            .form-control {
                padding-inline: 44px;
            }
        }

        &.size-sm {
            &.auto-width {
                width: 200px;
            }
            .search-icon, .clear-icon {
                width: 28px;
            }
            .form-control {
                padding-left: 28px;
            }
        }

        .search-icon, .clear-icon {
            font-size: larger;
        }

        .clear-icon {
            border-radius: var(--border-radius);
            color: var(--danger)
        }

        .form-control {
            &::placeholder {
                overflow: visible;
            }
        }
    }

    ::v-deep .search-results {
        width: 100%;
        border-radius: 10px;

        &.align-left .e-search-input {
            margin-right: auto;
        }
        &.align-center .e-search-input {
            margin-left: auto;
            margin-right: auto;
        }
        &.align-right .e-search-input {
            margin-left: auto;
        }
    }

    ::v-deep .cms-block {
        margin-bottom: var(--list-item-spacing);
        break-inside:avoid;
    }

    .search-results {
        height: var(--search-max-height);
        max-height: var(--search-max-height);
        overflow: hidden auto;
    }

    .cms-list-item {
        width: 100%;
    }

    &.layout-default {
        ::v-deep .cms-block:not(:last-child) {
            border-bottom: var(--divider-width) solid var(--divider-color);
        }
    }

    &.layout-cards {
        ::v-deep > .search-results {
            display: block;
            column-count: var(--list-item-columns);
            column-gap: var(--list-item-spacing);
            orphans: 1;
            widows: 1;
            line-height: 1;
        }
    }
}
</style>
